import type { ReactNode, RefObject } from "react";
import { forwardRef } from "react";

import type { Virtualizer } from "@tanstack/react-virtual";
import { useVirtualizer } from "@tanstack/react-virtual";

import type { IListItem, ItemContainerProps } from "./ItemContainer";
import { ItemContainer } from "./ItemContainer";

interface IListProps extends Pick<ItemContainerProps, "onItemTextChanged" | "ListItemComponent"> {
    items: Array<IListItem>;
    removeItem: (item: IListItem) => void;
    validateExistingItem: (index: number) => boolean;
    virtualizer: Virtualizer<Element, Element>;
}

const List = ({
    items,
    ListItemComponent,
    onItemTextChanged,
    removeItem,
    validateExistingItem,
    virtualizer,
}: IListProps) => (
    <ul
        className="item-list"
        style={{
            height: `${virtualizer.getTotalSize()}px`,
            paddingTop: `${virtualizer.getVirtualItems()[0]?.start ?? 0}px`,
            boxSizing: "border-box",
        }}
    >
        {virtualizer.getVirtualItems().map((virtualItem, index) => {
            const item = items[virtualItem.index];
            const isValid = validateExistingItem(index);

            return (
                <ItemContainer
                    onItemTextChanged={onItemTextChanged}
                    key={item.text}
                    item={item}
                    isValid={isValid}
                    ListItemComponent={ListItemComponent}
                    removeItem={() => removeItem(item)}
                />
            );
        })}
    </ul>
);

interface IListItemsProps extends Omit<IListProps, "virtualizer"> {
    footer?: ReactNode;
    items: Array<IListItem>;
    itemHeight: number;
}

export const ListItems = forwardRef(
    ({ footer, items, itemHeight, ...other }: IListItemsProps, ref: RefObject<HTMLDivElement | null>) => {
        // oxlint-disable-next-line react/incompatible-library -- @tanstack/react-virtual's re-render model is incompatible with memoization; no compiler transform is enabled in this repo, so this is safe
        const rowVirtualizer = useVirtualizer({
            count: items.length,
            getScrollElement: () => ref.current,
            estimateSize: () => itemHeight,
        });

        return (
            <div
                className="items-list-container"
                ref={ref}
                style={{ overflow: "auto" }}
            >
                <List
                    {...other}
                    items={items}
                    virtualizer={rowVirtualizer}
                />
                {footer}
            </div>
        );
    },
);
